import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes
} from 'aws-amplify/auth';

import { AuthContext, AuthContextType } from './authHelpers';

type BaseUser = {
  username: string;
  userId: string;
  attributes?: Record<string, string>;
};

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [currentUser, setCurrentUser] = useState<BaseUser | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  async function getAccessToken(): Promise<string> {
    try {
      // NOTE: Amplify automatically refreshes tokens as needed
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) {
        // Do NOT sign out here. Caller should handle "not authenticated yet".
        throw new Error('No access token (user may be mid sign-in)');
      }
      return token;
    } catch (e) {
      // Also do not sign out here.
      throw e instanceof Error ? e : new Error('Failed to fetch session');
    }
  }

  async function refreshCurrentUser(): Promise<void> {
    try {
      setCurrentUser(await getCurrentUser());
      await refreshGroups();
    } catch {
      // Mid sign-in or signed-out; keep currentUser as-is
    }
  }

  async function refreshGroups(): Promise<void> {
    try {
      const { tokens } = await fetchAuthSession();
      const cognitoGroups = tokens?.idToken?.payload?.['cognito:groups'];
      if (Array.isArray(cognitoGroups)) {
        setGroups(cognitoGroups as string[]);
      } else {
        setGroups([]);
      }
    } catch {
      setGroups([]);
    }
  }

  async function logout(): Promise<void> {
    try {
      // Use global sign out to revoke tokens on the server
      // This prevents any saved Bearer tokens from working after logout.
      await signOut({ global: true });
    } catch (error) {
      // Fallback to regular sign out if global fails
      console.warn('Global sign out failed, falling back to local sign out:', error);
      await signOut();
    }

    // Clear any cached tokens by forcing a refresh on next access.
    // (These tokens should be invalidated by the sign out above anyway.)
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch {
      // Expected to fail after sign out
    }

    setCurrentUser(null);
    setGroups([]);
  }

  async function safeFetchAttributes(): Promise<{ [k: string]: string | undefined;}> {
    try {
      const a = await fetchUserAttributes();
      return Object.fromEntries(Object.entries(a));
    } catch {
      throw new Error('Failed to fetch user attributes');
    }
  }

  async function debugTokens(): Promise<void> {
    try {
      console.log('Starting token debugging...');
      const session = await fetchAuthSession().catch(() => null);
      console.log('ID token payload:', session?.tokens?.idToken?.payload);
      console.log('Access token present:', Boolean(session?.tokens?.accessToken));

      const user = await getCurrentUser().catch(() => null);
      console.log('User:', user);
      const attrs = await safeFetchAttributes();
      console.log('User attributes:', attrs);
    } catch (error) {
      console.error('Error debugging tokens:', error);
    }
  }

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        setCurrentUser(await getCurrentUser());
        await refreshGroups();
      } catch {
        setCurrentUser(null);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const value: AuthContextType = {
    currentUser,
    debugTokens,
    getAccessToken,
    groups,
    loading,
    logout,
    refreshCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
